// ============================================================================
// View Registry — parte del feat_2026-05-24_rediseno-navegacion-backoffice
// ============================================================================
//
// Mapeo declarativo entre `view_key` (campo de sys_menu_items, inmutable por
// código) y el componente React que se monta en runtime.
//
// La URL pública es libre y editable desde la admin de menú; lo que ata
// "menu item → componente" es el `view_key`. Si un admin cambia la URL de
// `/administration/users` a `/empresa/socios`, el componente UserList se
// sigue montando porque el view_key `users.list` no cambió.
//
// Reglas:
//   1. Las claves son inmutables — cambiar una clave existente requiere
//      migración SQL para actualizar sys_menu_items.view_key.
//   2. Cada vista nueva del template debe registrarse aquí con su clave.
//   3. Las claves siguen `<module>.<screen>` en inglés, donde <module>
//      coincide con `sys_permissions.module` cuando aplica (no es obligatorio
//      coincidir 1:1 — un módulo puede tener varias vistas).
//   4. Las acciones (create, edit, status, delete) NO son view_keys: ocurren
//      dentro de la lista vía modal/sub-modal. Si se hace una página dedicada
//      para una acción (ej. `/users/new`), agregar `users.new` aquí.
//
// Ver: documentacion/feat_2026-05-24_rediseno-navegacion-backoffice.md
// ============================================================================

import { lazy, type LazyExoticComponent, type ComponentType } from 'react';

const VIEW_REGISTRY = {
  'dashboard.home':         lazy(() => import('views/dashboard')),
  'works.list':             lazy(() => import('views/works/List')),
  // Obras del Administrador de ASF: copia sin la pestaña de Carga de datos
  // (db/085). Es una vista aparte y no un condicional, por decision del
  // usuario: asi la pestaña no puede reaparecer por un permiso mal dado.
  'works.asf.list':         lazy(() => import('views/works-asf/List')),
  'users.list':             lazy(() => import('views/users/List')),
  'profiles.list':          lazy(() => import('views/profiles/List')),
  'sessions.list':          lazy(() => import('views/security/Sessions')),
  'audit-logs.list':        lazy(() => import('views/audit-logs/List')),
  'menu-items.list':        lazy(() => import('views/menu-items/List')),
  // Que ve este usuario (db/177): pantalla de SOLO LECTURA que explica el
  // alcance de una persona. La ven Administrador y, acotada a su equipo por
  // el API, la Directora de area.
  'user-scope.list':        lazy(() => import('views/user-scope/List')),
  'business-config.list':   lazy(() => import('views/business-config/List')),
  'email-senders.list':     lazy(() => import('views/email-senders/List')),
  'email-templates.list':   lazy(() => import('views/email-templates/List')),
  'email-logs.list':        lazy(() => import('views/email-logs/List')),
  'email-witnesses.list':   lazy(() => import('views/email-witnesses/List')),
  'system-config.list':     lazy(() => import('views/system-config/List')),
  'security.throttle-web':  lazy(() => import('views/security/RateLimitWeb')),
  'tracking-visits.list':   lazy(() => import('views/tracking-visits/List')),
  'chatbot.list':           lazy(() => import('views/chatbot/List')),
  'work-categories.list':   lazy(() => import('views/catalogs/work-categories/List')),
  'drone-pilots.list':      lazy(() => import('views/drone-pilots/List')),
  // Comentarios de Terrasat de todas las obras: crear, editar y borrar (db/210).
  'terrasat-comments.list': lazy(() => import('views/terrasat-comments/List')),
  'teams.list':             lazy(() => import('views/teams/List')),
  // Asignación de obras en cascada (db/182): reemplaza a Estructuras.
  'delegations.list':       lazy(() => import('views/delegations/List')),
  'audit-panel.list':       lazy(() => import('views/auditor/List')),
  'flight-panel.list':      lazy(() => import('views/pilot/List')),
  'field-visibility.list':  lazy(() => import('views/field-visibility/List')),
  'processing.list':        lazy(() => import('views/processing/List')),
} as const satisfies Record<string, LazyExoticComponent<ComponentType<any>>>;

export type ViewKey = keyof typeof VIEW_REGISTRY;

/** Devuelve el componente registrado o `null` si la clave no existe */
export function resolveView(key: string | null | undefined): LazyExoticComponent<ComponentType<any>> | null {
  if (!key) return null;
  return (VIEW_REGISTRY as Record<string, LazyExoticComponent<ComponentType<any>>>)[key] ?? null;
}

/** Lista de claves disponibles — usada en el form de menú para el selector */
export function listViewKeys(): readonly ViewKey[] {
  return Object.keys(VIEW_REGISTRY).sort() as ViewKey[];
}

export default VIEW_REGISTRY;
